import { makeStyles } from "@mui/styles";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Actions
import { VollmachtRequestFormAction } from "../../../store/actions/vorgange/VollmachtRequestFormAction";

// Core Components
import MDCButton from "../../core/MDCButton";
import MDCModal from "../../core/MDCModal";

// Icons
import carLogo from "../../../assets/images/my_digital_car.svg";

// Material UI
import Grid from "@mui/material/Grid";
import MDCLoader from "../../core/MDCLoader";
import MDCText from "../../core/MDCText";
import MDCLabel from "../../core/MDCLabel";
import ErrorText from "../../shared/ErrorText";

// Styles
const useStyles = makeStyles((theme) => ({
  headerCss: {
    padding: "2rem",
    alignItems: "center",
  },
  headerColor: { color: theme.palette.MDCColors.black },
  centerLoader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const SignatureSendEmailPopupNP = ({
  show,
  handlePopupClose,
  requestId,
  vollmatchtType,
  vorname,
  nachname,
  contactEmail: initialContactEmail,
  halterType,
}) => {
  const dispatch = useDispatch();

  const [contactEmail, setContactEmail] = useState(initialContactEmail || "");
  const [rows, setRows] = useState([
    {
      vorname: vorname || "",
      nachname: nachname || "",
      emailadresse: "",
    },
  ]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Update rows when vorname or nachname props change
  useEffect(() => {
    if (show) {
      // Prefill name
      setRows([
        {
          vorname: vorname || "",
          nachname: nachname || "",
          emailadresse: "",
        },
      ]);

      // Prefill contact email
      setContactEmail(initialContactEmail || "");
    }
  }, [show, vorname, nachname, initialContactEmail]);

  const { headerCss, headerColor, centerLoader } = useStyles();

  const handleChange = (id, field, value) => {
    setRows(
      rows.map((row, index) =>
        index === id ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Now sending data at Back-End
    setLoading(true);

    const requestData = {
      halterType: halterType,
      contactEmail,
      verpflichtendeUnterzeichner: rows,
    };

    dispatch(
      VollmachtRequestFormAction.sendSignatureEmailDetails(
        vollmatchtType,
        requestId,
        requestData
      )
    ).then((resp) => {
      if (resp?.status === 200) {
        toast.success(resp?.data?.message);
        handlePopupClose();
      } else if (resp?.response?.status === 400) {
        const { errorFieldMap } = resp?.response?.data;
        setErrors(errorFieldMap || {});
      }
      setLoading(false);
    });
  };

  const getModalHeader = () => {
    return (
      <Grid container className={headerCss}>
        <Grid item xs={9} className={headerColor}>
          <h4>Externer Signaturprozess</h4>
        </Grid>
        <Grid item xs={3}>
          <img alt="My Digital Car" src={carLogo} width="100%" />
        </Grid>
      </Grid>
    );
  };

  const getModalBody = () => {
    return (
      <Grid marginX={"2rem"} marginBottom={"2rem"}>
        <Grid
          container
          className={loading && centerLoader}
          minHeight={"20rem"}
          spacing={1}
        >
          {loading ? (
            <MDCLoader />
          ) : (
            <>
              <Grid item xs={"12"} md={12} mt="1.5rem">
                <MDCLabel label="Emailadresse für Kontaktaufnahme" />
                <MDCText
                  name="contactEmail"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  error={errors && errors["contactEmail"]}
                  readOnly
                />
                <ErrorText error={errors && errors["contactEmail"]} />
              </Grid>
              <Grid item xs={"12"}>
                <MDCLabel label="Verpflichtete unterzeichnende Person" />
                {rows.map((row, index) => (
                  <Grid
                    container
                    key={index}
                    spacing={0.5}
                    marginTop={"0.5rem"}
                    marginBottom={"0.5rem"}
                    display={"flex"}
                    justifyContent={"space-between"}
                  >
                    <Grid item xs={4} marginBottom="0.5rem">
                      <MDCLabel label="Vorname" isReadOnly />
                      <MDCText
                        value={row.vorname}
                        onChange={(e) =>
                          handleChange(index, "vorname", e.target.value)
                        }
                        error={
                          errors &&
                          errors[
                            `verpflichtendeUnterzeichner[${index}].vorname`
                          ]
                        }
                        readOnly
                      />
                      <ErrorText
                        error={
                          errors &&
                          errors[
                            `verpflichtendeUnterzeichner[${index}].vorname`
                          ]
                        }
                      />
                    </Grid>

                    <Grid item xs={4} marginBottom="0.5rem">
                      <MDCLabel label="Nachname" isReadOnly />
                      <MDCText
                        value={row.nachname}
                        onChange={(e) =>
                          handleChange(index, "nachname", e.target.value)
                        }
                        error={
                          errors &&
                          errors[
                            `verpflichtendeUnterzeichner[${index}].nachname`
                          ]
                        }
                        readOnly
                      />
                      <ErrorText
                        error={
                          errors &&
                          errors[
                            `verpflichtendeUnterzeichner[${index}].nachname`
                          ]
                        }
                      />
                    </Grid>

                    <Grid item xs={4} marginBottom="0.5rem">
                      <MDCLabel label="Emailadresse" isReadOnly />
                      <MDCText
                        value={row.emailadresse}
                        onChange={(e) =>
                          handleChange(index, "emailadresse", e.target.value)
                        }
                        error={
                          errors &&
                          errors[
                            `verpflichtendeUnterzeichner[${index}].emailadresse`
                          ]
                        }
                      />
                      <ErrorText
                        error={
                          errors &&
                          errors[
                            `verpflichtendeUnterzeichner[${index}].emailadresse`
                          ]
                        }
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    );
  };

  const getModalFooter = () => {
    return (
      <Grid container pb={3} paddingX={"2rem"} justifyContent="space-between">
        <Grid item>
          <MDCButton
            onClick={handlePopupClose}
            variant="outlined"
            label="Schließen"
          />
        </Grid>

        <Grid item>
          <MDCButton
            onClick={handleSubmit}
            variant="contained"
            label="Anforderung senden"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <MDCModal
      width="90%"
      open={show}
      title={getModalHeader()}
      body={getModalBody()}
      footer={getModalFooter()}
      isScroll
    />
  );
};

export default SignatureSendEmailPopupNP;
